import { useEffect, useState } from 'react';
import { Box, Typography, Skeleton, Alert, Button } from '@mui/material';
import { Inventory2Outlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import publicationService from '../../my-publications/services/publicationService';
import { UserPublication } from '../../my-publications/types/publication.types';
import { PublicationCard } from '../../my-publications/components/PublicationCard';

interface SectionHeaderProps {
  showViewAll: boolean;
}

function SectionHeader({ showViewAll }: Readonly<SectionHeaderProps>) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mb: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: '16px',
            bgcolor: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Inventory2Outlined sx={{ color: 'white', fontSize: 24 }} />
        </Box>
        <Typography variant="h6" fontWeight={700} data-testid="my-recent-publications-title">
          {t('home.publications.title')}
        </Typography>
      </Box>
      {showViewAll && (
        <Button
          variant="text"
          size="small"
          onClick={() => navigate('/my-publications')}
          data-testid="recent-publications-view-all"
          sx={{ fontWeight: 500, textTransform: 'none' }}
        >
          {t('home.publications.viewAll')}
        </Button>
      )}
    </Box>
  );
}

export function MyRecentPublications() {
  const { t } = useTranslation();
  const [publications, setPublications] = useState<UserPublication[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadPublications = async () => {
      try {
        setLoading(true);
        const result = await publicationService.getUserPublications({ page: 1, limit: 3 });
        setPublications(result.data);
        setTotal(result.meta.total);
        setError(null);
      } catch (err) {
        console.error('Failed to load publications:', err);
        setError(t('home.publications.errorLoading'));
      } finally {
        setLoading(false);
      }
    };

    loadPublications();
  }, [t]);

  if (loading) {
    return (
      <Box data-testid="recent-publications-loading">
        <SectionHeader showViewAll={false} />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {[1, 2, 3].map(i => (
            <Box
              key={i}
              sx={{
                display: 'flex',
                gap: 2,
                p: 2,
                borderRadius: 2,
                bgcolor: 'background.paper',
                boxShadow: 1,
              }}
            >
              <Skeleton
                variant="rectangular"
                width={90}
                height={90}
                sx={{ borderRadius: 2, flexShrink: 0 }}
              />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width="70%" height={24} />
                <Skeleton variant="text" width="50%" height={20} />
                <Skeleton variant="text" width="60%" height={20} />
                <Skeleton
                  variant="rectangular"
                  width={60}
                  height={24}
                  sx={{ borderRadius: 4, mt: 0.5 }}
                />
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <SectionHeader showViewAll={false} />
        <Alert severity="error" data-testid="recent-publications-error">
          {error}
        </Alert>
      </Box>
    );
  }

  // AC-039.9: hidden entirely when user has no publications
  if (total === 0) {
    return null;
  }

  return (
    <Box data-testid="home-my-publications-section">
      <SectionHeader showViewAll={total > 3} />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {publications.map(pub => (
          <PublicationCard key={pub.id} publication={pub} />
        ))}
      </Box>
    </Box>
  );
}
